import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const SocialHeavyTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center bg-cyan-50/30 text-slate-800"
      style={{
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-cyan-100 overflow-hidden grid grid-cols-1 md:grid-cols-12">
        {/* Left Social Proof Heavy Column */}
        <div className="md:col-span-5 p-6 md:p-8 bg-gradient-to-b from-cyan-50/70 to-blue-50/50 border-b md:border-b-0 md:border-r border-cyan-100 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 object-contain" />}
              <span className="flex items-center gap-1 text-[10px] font-black uppercase text-cyan-800 bg-cyan-100 px-2.5 py-0.5 rounded-full">
                <svg className="w-3 h-3 text-cyan-700 fill-current" viewBox="0 0 24 24">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                4.9/5 Verified Rating
              </span>
            </div>

            <div>
              <p className="text-xs font-bold text-cyan-700 uppercase tracking-wider">Top Rated Choice</p>
              <h2 className="text-2xl font-black text-slate-900 mt-0.5">{plan.tier}</h2>
              {plan.description && <p className="text-xs text-slate-600 mt-1">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />

            <div className="pt-1">
              <TestimonialCard testimonial={config.testimonial || {
                quote: "Subscale transformed our checkout conversion rate by 34% within the first week.",
                author: "Sarah Jenkins",
                role: "Head of Growth, Acme",
                avatarUrl: ""
              }} />
            </div>

            <PlanFeatureList features={plan.features} checkColorClass="text-cyan-600" />
          </div>

          <PriceBreakdown plan={plan} billingCycle={billingCycle} addonsTotal={addonsTotal} discountAmount={discountAmount} finalTotal={finalTotal} currencySymbol={currencySymbol} />
        </div>

        {/* Right Form */}
        <div className="md:col-span-7 p-6 md:p-8 space-y-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{config.headerMain || 'Join 10,000+ Happy Teams'}</h1>
            <p className="text-xs text-slate-500">{config.headerSub || 'Setup takes under 2 minutes.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields config={config} formData={formData} onInputChange={onInputChange} />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-4 text-xs font-black uppercase tracking-wider text-white shadow-lg transition-all hover:opacity-90 active:scale-[0.99] disabled:opacity-50"
              style={{
                backgroundColor: config.primaryColor || '#0891b2',
                borderRadius: config.buttonRadius || '10px',
              }}
            >
              {isCapturing ? 'Submitting...' : config.buttonText || `Get Started Now (${currencySymbol}${finalTotal.toFixed(2)})`}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
